import * as React from 'react'
import { useEffect } from 'react'
import { Field } from 'formik'
import {
  AddressFields,
  BaseTextField,
  MuiPhoneFieldFormik,
} from '@user/components/Formik'
import MenuItem from '@mui/material/MenuItem'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import {
  CreateLocationFormFieldProps,
  CreateServiceLocationFormModel,
} from './utility/types'
import { useForm } from './hooks/useForm'
import { useFormHandlers } from './hooks/useFormHandlers'
import { BadgeIcon, PlusIcon, PlusIconDisabled } from '@sales-center/components'
import Stack from '@mui/material/Stack'
import { EmailCollection } from '@user/components/EmailCollection/EmailCollection'
import Button from '@mui/material/Button/Button'
import { useTheme } from '@mui/material/styles'
import DeleteIcon from '@mui/icons-material/Delete'

export const FormFields: React.FC<CreateLocationFormFieldProps> = (props) => {
  const { hideOptional, branches, locationTypes, divisions } = props

  const path = !!props.path ? `${props.path}.` : ''

  const {
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    location,
    locationLabel,
    requireEmail,
    requirePhone,
    nameRequired,
  } = useForm(props)

  const {
    isMobilePhone,
    isMobileAltPhone,
    addEmailAddress,
    addEmailDisabled,
    onBlurPhone,
    handleEmailChange,
  } = useFormHandlers(location)

  useEffect(() => {
    Object.keys(location).forEach((field) => {
      setFieldTouched(`${path}${field}`, true)
    })

    location?.emailAddresses.forEach((_, index) => {
      setFieldTouched(`${path}emailAddresses[${index}].emailAddress`, true)
    })
  }, [])
  const theme = useTheme()
  return (
    <>
      <Box
        sx={{ marginTop: theme.spacing(3.2), padding: `0 ${theme.spacing(2)}` }}
      >
        <Typography variant="h7" sx={{ fontWeight: '600' }}>
          Location Information
        </Typography>
      </Box>
      <Box
        sx={{ marginTop: theme.spacing(1), padding: `0 ${theme.spacing(2)}` }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'start',
            marginBottom: theme.spacing(2.4),
          }}
        >
          <Box>
            <BadgeIcon />
          </Box>
          <Box
            sx={{
              marginTop: theme.spacing(0.3),
              marginLeft: theme.spacing(0.5),
            }}
          >
            <Typography variant="body2">
              {(location.billingLocation ? 'Bill-to' : 'Service') +
                ' Location: '}{' '}
              {locationLabel}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Grid
        container
        spacing={1.6}
        padding={2}
        paddingTop={0}
        paddingBottom={0}
      >
        <Grid item xs={12} sx={{ marginTop: theme.spacing(0.8) }}>
          <Field
            name={`${path}branchId`}
            label="Branch"
            component={BaseTextField}
            size="small"
            select
            required
            disabled={isSubmitting}
          >
            {branches.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Field>
        </Grid>

        {!hideOptional && (
          <Grid item xs={12}>
            <Field
              name={`${path}locationTypeId`}
              label="Location Type"
              component={BaseTextField}
              size="small"
              select
              disabled={isSubmitting}
            >
              {locationTypes.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Field>
          </Grid>
        )}

        {!hideOptional && (
          <Grid item xs={12}>
            <Field
              name={`${path}divisionId`}
              label="Division"
              component={BaseTextField}
              size="small"
              select
              disabled={isSubmitting}
            >
              {divisions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Field>
          </Grid>
        )}

        <Grid item xs={6}>
          <Field
            name={`${path}firstName`}
            label="First Name"
            component={BaseTextField}
            size="small"
            required={nameRequired}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value
              setFieldTouched(`${path}company`, true)
              setFieldValue(`${path}firstName`, value)
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Field
            name={`${path}lastName`}
            label="Last Name"
            component={BaseTextField}
            size="small"
            required={nameRequired}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value
              setFieldTouched(`${path}company`, true)
              setFieldValue(`${path}lastName`, value)
            }}
          />
        </Grid>

        {!hideOptional && (
          <Grid item xs={12}>
            <Field
              name={`${path}company`}
              label="Company"
              component={BaseTextField}
              size="small"
              required={nameRequired}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value
                setFieldTouched(`${path}firstName`, true)
                setFieldTouched(`${path}lastName`, true)
                setFieldValue(`${path}company`, value)
              }}
            />
          </Grid>
        )}

        <AddressFields
          textFieldProps={{ size: 'small' }}
          disabled={isSubmitting}
          fieldNamePrefix={props.path + '.'}
          setFieldTouched={setFieldTouched}
          spacing={1.6}
          required
        />

        {!hideOptional && (
          <>
            <Grid
              item
              xs={12}
              style={{
                marginTop: theme.spacing(0),
                paddingTop: theme.spacing(0),
              }}
            >
              <EmailCollection<CreateServiceLocationFormModel>
                removeIcon={<DeleteIcon sx={{ color: '#AB1010' }} />}
                onChangeHandler={handleEmailChange}
                name={`${path}emailAddresses`}
                required={requireEmail}
                radioPaddingTop={16}
              />
            </Grid>

            <Grid
              item
              xs={12}
              style={{
                marginTop: theme.spacing(0),
                paddingTop: theme.spacing(1),
              }}
            >
              <Button
                variant="text"
                disabled={addEmailDisabled}
                onClick={addEmailAddress}
                startIcon={
                  addEmailDisabled ? <PlusIconDisabled /> : <PlusIcon />
                }
                sx={{
                  color: addEmailDisabled ? '' : '#1565c0',
                  fontFamily: 'Source Sans Pro SemiBold',
                  minWidth: '230px',
                }}
              >
                Add additional email address
              </Button>
            </Grid>
          </>
        )}

        <Grid
          item
          xs={!hideOptional ? 9 : 12}
          style={{
            paddingTop: hideOptional ? '16px' : '24px',
          }}
        >
          <Field
            name={`${path}phoneNumber`}
            label="Phone"
            component={MuiPhoneFieldFormik}
            size="small"
            margin="none"
            required={requirePhone}
            onBlur={onBlurPhone}
            helperText={isMobilePhone ? 'Number entered is Mobile' : ''}
          />
        </Grid>

        {!hideOptional && (
          <Grid
            item
            xs={3}
            style={{
              paddingTop: theme.spacing(2.4),
            }}
          >
            <Field
              name={`${path}phoneExt`}
              label="Ext"
              component={BaseTextField}
              size="small"
              disabled={!location.phoneNumber}
            />
          </Grid>
        )}

        {!hideOptional && (
          <Grid item xs={9}>
            <Field
              name={`${path}altPhoneNumber`}
              label="Alternate Phone"
              component={MuiPhoneFieldFormik}
              margin="none"
              size="small"
              onBlur={onBlurPhone}
              helperText={isMobileAltPhone ? 'Number entered is Mobile' : ''}
            />
          </Grid>
        )}

        {!hideOptional && (
          <Grid item xs={3}>
            <Field
              name={`${path}altPhoneExt`}
              label="Ext"
              component={BaseTextField}
              size="small"
              disabled={!location.altPhoneNumber}
            />
          </Grid>
        )}
      </Grid>
    </>
  )
}
FormFields.displayName = 'FormFields'
